import { useSelector } from "react-redux";
import { useMyPermissions } from "../api/queries/adminToolsQueries";
import useCurrentUser from "./useCurrentUser";

/**
 * Role permissions from GET /permissions/me (strict: role names grant nothing).
 * - superAdmin → everything.
 * - configured=false → no HR-portal modules at all (Layout shows the "not set up" page).
 * - can(module, "view" | "modify") → modules[module].view / .modify.
 * - companyWide(module, action) → that, with COMPANY scope: HR-level screens and
 *   actions (company tiles, master data, approval rules, data tools, settings…).
 *   BRANCH scope = branch-manager rules on the same screens.
 * hasHrAccess → may open HR view (superAdmin or any module view); employeeId → may open My view.
 * Module keys: employees, company-setup, users, attendance, dtr, overtime, leave,
 * assets, performance, learning, audit-logs, data-tools.
 */
const usePermissions = () => {
  const user = useCurrentUser();
  const token = useSelector((s) => s.auth.accessToken);
  const query = useMyPermissions({
    userId: user.userId,
    token,
    enabled: !!user.userId,
  });
  const p = query.data;
  const superAdmin = !!p?.superAdmin;
  const configured = superAdmin || !!p?.configured;

  const scopeOf = (module) =>
    superAdmin ? "COMPANY" : p?.modules?.[module]?.scope || null;
  const can = (module, action = "view") => {
    if (superAdmin) return true;
    if (!configured) return false;
    const m = p?.modules?.[module];
    if (!m) return false;
    return action === "modify" ? !!m.modify : !!(m.view || m.modify);
  };
  const companyWide = (module, action = "view") =>
    superAdmin || (can(module, action) && scopeOf(module) === "COMPANY");
  /** Company-wide in at least one module (e.g. company branch lists in pickers). */
  const anyCompanyWide =
    superAdmin ||
    Object.values(p?.modules || {}).some(
      (m) => (m.view || m.modify) && m.scope === "COMPANY",
    );
  /** null = all employees; "Internal" / "Deployed" = the role only sees that type in this module. */
  const employeeTypeOf = (module) =>
    superAdmin ? null : p?.modules?.[module]?.employeeType || null;
  /** Any of these modules viewable? */
  const canAny = (...modules) => modules.some((m) => can(m));
  // Unified app (My view / HR view). The server sends hasHrAccess and employeeId;
  // until that's deployed: super admin or any module viewable, and the token's employeeId.
  const hasHrAccess =
    !!p &&
    (typeof p.hasHrAccess === "boolean"
      ? p.hasHrAccess
      : superAdmin ||
        Object.values(p.modules || {}).some((m) => m.view || m.modify));
  const employeeId =
    (p && p.employeeId !== undefined ? p.employeeId : user.employeeId) || null;

  return {
    can,
    canAny,
    companyWide,
    anyCompanyWide,
    configured,
    superAdmin,
    hasHrAccess,
    employeeId,
    scopeOf,
    employeeTypeOf,
    // Branches this user manages (their sub-branches count as "under them" too)
    managedBranchIds: p?.managedBranchIds || [],
    isLoading: !!user.userId && query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export default usePermissions;

/**
 * May the user change things in this module? modules[module].modify;
 * { company: true } also requires COMPANY scope (HR-level actions).
 */
export const useCanModify = (module, { company = false } = {}) => {
  const { can, companyWide } = usePermissions();
  return company ? companyWide(module, "modify") : can(module, "modify");
};
